import { EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID } from 'constants/experience'
import { type ExperienceListFilters } from 'actions/ExperienceActions'
import { ExperiencesCheckoutTopOfferListId } from 'checkout/selectors/experienceSelectors'

export interface ComputeParams {
  currencyCode: string
  geoCoords: { latitude?: number, longitude?: number }
  hotelOffer?: { id: string, experiencesCurated?: boolean }
  availability: { from?: string, to?: string }
  hasNoIncludedTransfer: boolean
  targetAudience?: App.ExperienceTargetAudience
  experienceIdsTagsAssignments?: Array<string>
}

export function computeTopOfferListFiltersFromParams(
  params: ComputeParams,
): Array<{ id: ExperiencesCheckoutTopOfferListId, filters: ExperienceListFilters }> {
  const {
    currencyCode,
    geoCoords,
    hotelOffer,
    availability,
    hasNoIncludedTransfer,
    targetAudience,
    experienceIdsTagsAssignments,
  } = params
  const { latitude, longitude } = geoCoords
  if (!latitude || !longitude) return []

  const list: Array<{ id: ExperiencesCheckoutTopOfferListId, filters: ExperienceListFilters }> = []

  // Priority 1: Offers with tags assigned list
  // This implementation is temporary.
  // After the best of, svc-experiences will have a filter to return
  // offers with tags assigned.
  if (experienceIdsTagsAssignments?.length) {
    list.push({
      id: 'TAG_ASSIGNED',
      filters: {
        currencyCode,
        experienceIds: experienceIdsTagsAssignments,
        ...availability,
      },
    })
  }

  // Priority 2: Transfers & Water Transfers & Lounges
  if (hasNoIncludedTransfer) {
    list.push({
      id: 'TRANSFER',
      filters: {
        currencyCode,
        latitude,
        longitude,
        distanceRange: ['0KM', '50KM'],
        categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID],
        limit: 1,
        ...availability,
      },
    })
  }

  list.push({
    id: 'WATER_TRANSFER',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 1,
      ...availability,
    },
  })

  list.push({
    id: 'LOUNGE',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
      limit: 1,
      ...availability,
    },
  })

  // Priority 3: Hotel Offer Curated List
  if (hotelOffer?.experiencesCurated) {
    list.push({
      id: 'HOTEL_CURATED',
      filters: {
        currencyCode,
        offerId: hotelOffer.id,
        showUnlisted: true,
        limit: 2,
        ...availability,
      },
    })
  }

  // Priority 4: Regional Offer Curated List
  list.push({
    id: 'REGIONAL_CURATED',
    filters: {
      currencyCode,
      latitude,
      longitude,
      onlyCuratedByRegion: true,
      limit: 3,
      ...availability,
    },
  })

  // Priority 5: Distance based lists
  const excludeCats = [
    EXP_CATEGORY_AIRPORT_TRANSFER_ID,
    EXP_CATEGORY_AIRPORT_LOUNGE_ID,
    EXP_CATEGORY_WATER_TRANSFER_ID,
  ]

  list.push({
    id: 'DISTANCE_SHORT',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', '5KM'],
      targetAudienceIn: targetAudience ? [targetAudience] : undefined,
      categoryCodesNotIn: excludeCats,
      limit: 2,
      ...availability,
    },
  })

  list.push({
    id: 'DISTANCE_MEDIUM',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['5KM', '20KM'],
      targetAudienceIn: targetAudience ? [targetAudience] : undefined,
      categoryCodesNotIn: excludeCats,
      limit: 2,
      ...availability,
    },
  })

  list.push({
    id: 'DISTANCE_LONG',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['20KM', '50KM'],
      targetAudienceIn: targetAudience ? [targetAudience] : undefined,
      categoryCodesNotIn: excludeCats,
      limit: 2,
      ...availability,
    },
  })

  // Priority 6: Fallback List
  list.push({
    id: 'FALLBACK',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', '50KM'],
      targetAudienceIn: targetAudience ? [targetAudience] : undefined,
      categoryCodesNotIn: excludeCats,
      limit: 30,
      ...availability,
    },
  })

  return list
}
export default computeTopOfferListFiltersFromParams
